/**
 * Attempt moderation — the transition table and confirm-dialog copy for the four Team Leader
 * moderation actions, plus the attempt-status vocabulary the roster filters on.
 *
 * Extracted from `exams_.$examId.tsx` when the workspace was split into tabs, so the roster, the
 * Student Sheet and any future surface cannot disagree about which transitions are legal or about
 * what an action actually does. Nothing here decides authorization: `canGradeRow` reads the
 * server's answer and the two `can*Attempt` guards mirror the server's own preconditions as
 * affordances only. Every action is re-checked server-side and its refusal surfaced verbatim.
 */
import type { TlExamDetailStudentRow } from "@/lib/api/tl-exams";

/**
 * Backend `ExamSessionStatus` enum names, verbatim. Every roster row always has a session (rows are
 * built from ExamSessions server-side), so there is no "NotStarted" member.
 *
 * This is also the exact vocabulary the roster's `status` filter accepts: Phase 1.5-B matches the
 * supplied value against `Enum.GetNames<ExamSessionStatus>()` (case-insensitively) and ignores
 * anything else, so these five strings are the only ones that filter rather than silently no-op.
 */
export const ATTEMPT_STATUSES = [
  "InProgress",
  "Completed",
  "PendingGrading",
  "Abandoned",
  "Terminated",
] as const;

export type AttemptStatus = (typeof ATTEMPT_STATUSES)[number];

export function isAttemptStatus(value: string | null | undefined): value is AttemptStatus {
  return !!value && (ATTEMPT_STATUSES as readonly string[]).includes(value);
}

/** Spaced-out label for an attempt status. The raw enum name is echoed for anything unrecognised. */
export function attemptStatusLabel(status: string): string {
  switch (status) {
    case "InProgress":
      return "In Progress";
    case "PendingGrading":
      return "Pending Grading";
    default:
      return status;
  }
}

export function attemptStatusBadgeClass(status: string): string {
  switch (status) {
    case "Completed":
      return "bg-success/15 text-success border-success/30";
    case "InProgress":
      return "bg-primary/10 text-primary border-primary/30";
    case "PendingGrading":
      return "bg-warning/15 text-warning border-warning/30";
    case "Abandoned":
      return "bg-muted text-muted-foreground border-border";
    case "Terminated":
      return "bg-destructive/15 text-destructive border-destructive/30";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

// ── Superseded passes ─────────────────────────────────────────────────────────
// A sitting whose pass was voided for progression keeps its score and its `passed` flag forever, so
// every surface that renders "Passed" off the raw flag renders a green tick next to a student who is
// still locked out of the content behind that exam. That contradiction had no representation at all
// on screen — the marker lived in a column no DTO carried — which is how a checkpoint could report
// "not passed" while the roster three clicks away showed 100%. These two are the shared vocabulary
// for saying it once, the same way, everywhere it is shown.

export interface SupersededPassFacts {
  progressionSupersededAt?: string | null;
  progressionSupersededReason?: string | null;
}

/**
 * Whether this attempt's pass has been voided for progression.
 *
 * Deliberately NOT conditioned on `passed`: the marker is only ever written to a sitting that was
 * counting, but "counting" can come from the score clearing the threshold with the flag left unset,
 * and the browser does not know the threshold. Trust the marker.
 */
export function isSupersededPass(row: SupersededPassFacts | null | undefined): boolean {
  return !!row?.progressionSupersededAt;
}

/** Short badge text. Paired with — never a replacement for — the pass/fail label beside it. */
export const SUPERSEDED_PASS_LABEL = "Pass reset";

/**
 * The full explanation, for a `title` tooltip. `when` is a pre-formatted date supplied by the
 * caller, because date formatting differs per surface (and doing it here would risk an SSR/client
 * locale mismatch); omit it and the sentence simply reads without one.
 */
export function supersededPassTooltip(
  row: SupersededPassFacts,
  when?: string | null,
): string {
  const parts = [
    "This pass no longer counts towards progression — a new attempt was authorized over it, so the " +
      "content behind this exam stays locked until the student passes again. The attempt itself, " +
      "including its score, is unchanged.",
  ];
  if (when) parts.push(`Reset ${when}.`);
  if (row.progressionSupersededReason) parts.push(`Reason: ${row.progressionSupersededReason}`);
  return parts.join(" ");
}

/**
 * Whether the caller may grade this attempt, per the SERVER.
 *
 * `canGrade` is decided backend-side by `IAccessScopeService.GetGradeAuthorizationAsync`; this only
 * resolves the one case the field cannot express — an older API build that predates it, where the
 * field is absent entirely. There, fall back to "offer it and let the server decide", which is the
 * pre-existing behaviour: the grading endpoints have always enforced their own scope and return a
 * precise 403 the dialog surfaces. Treating absent-as-forbidden would silently remove grading from
 * every row on such a build.
 *
 * This function deliberately contains NO authorization logic of its own.
 */
export function canGradeRow(row: TlExamDetailStudentRow): boolean {
  return row.canGrade ?? true;
}

// ── Attempt-state guards ──────────────────────────────────────────────────────
// One place each, mirroring the server's own preconditions, so the roster and any other surface
// cannot disagree about which transitions are legal.

/**
 * Whether either reattempt operation may run against this attempt's STATUS.
 *
 * Both share one precondition — the student must not currently be mid-sitting or waiting on a
 * grader, because either state holds its own lock and authorizing another attempt on top of it
 * strands them (`ExamService.ApplyReattemptAsync`). Any finished attempt qualifies.
 */
export function canReattemptFromStatus(attemptStatus: string | null | undefined): boolean {
  if (!attemptStatus) return false;
  const s = attemptStatus.toLowerCase().replace(/_/g, "");
  return ["completed", "abandoned", "terminated"].includes(s);
}

/**
 * `ExamService.ForceSubmitForTeamLeaderAsync`: InProgress ONLY.
 *
 * PendingGrading used to be accepted and it was a silent lie — `CompleteExamAsync` short-circuits on
 * any non-InProgress status, so the endpoint answered 200 while the sitting stayed exactly where it
 * was. The paper is already in by then; the action that actually finalizes it is grading its essays.
 */
export function canForceSubmitAttempt(attemptStatus: string | null | undefined): boolean {
  if (!attemptStatus) return false;
  return attemptStatus.toLowerCase().replace(/_/g, "") === "inprogress";
}

// ── The final-exam business rule ──────────────────────────────────────────────
// A final exam may NEVER be reattempted with a content rewatch. That rule is enforced inside
// `ExamService.ApplyReattemptAsync` and is not negotiable from here: everything below reads a
// server-computed answer, so hiding a menu item is an affordance and never the enforcement. A client
// that called the endpoint anyway is refused with the same reason string.

/** The final categories the server can report. `WeekFinal` has no schema representation yet. */
export const EXAM_FINAL_CATEGORIES = ["None", "CourseFinal", "TrackFinal", "WeekFinal"] as const;

export type ExamFinalCategory = (typeof EXAM_FINAL_CATEGORIES)[number];

/** Operator-facing name for a final category. */
export function examFinalCategoryLabel(category: string | null | undefined): string {
  switch (category) {
    case "CourseFinal":
      return "course final";
    case "TrackFinal":
      return "track final";
    case "WeekFinal":
      return "week final";
    default:
      return "exam";
  }
}

/**
 * Whether the rewatch reattempt may be OFFERED for this row.
 *
 * Reads the server's answer (`canReattemptWithRewatch`) rather than deriving one, because the two
 * inputs are both invisible to the browser: whether the exam is a final is resolved from
 * `Course.FinalExamId` / `Track.FinalExamId` as well as the type column, and checkpoint anchoring is
 * structural with no member on the type enum to express an in-video gate.
 *
 * Absent — an older API build that predates the field — falls back to offering it, this file's
 * existing convention (see `canGradeRow`). The server still refuses, with a precise message the
 * dialog surfaces verbatim, so the fallback cannot let a final through.
 */
export function canOfferRewatchReattempt(
  row:
    | {
        canReattemptWithRewatch?: boolean | null;
        isCheckpointAnchored?: boolean | null;
      }
    | null
    | undefined,
): boolean {
  if (!row) return false;
  if (row.canReattemptWithRewatch != null) return row.canReattemptWithRewatch;
  // Older build: fall back to the one signal it did send. Anchoring alone cannot rule out a final,
  // which is exactly why the server re-checks.
  return row.isCheckpointAnchored ?? true;
}

/**
 * Whether the standalone "reset the content after this exam" operation may be offered.
 *
 * Structurally the same question as `canOfferRewatchReattempt` — both need a gate to define "after",
 * and neither means anything on a final — but read from its OWN server field rather than inferred
 * from that one. The two are separately authorized endpoints, so concluding one from the other is
 * precisely the client-side policy this file exists to avoid.
 *
 * Absent falls back to the rewatch flag, then to anchoring: on a build that predates this field the
 * rewatch flag answers the identical structural question, which is a better fallback than "offer it
 * blindly". The server refuses either way.
 */
export function canOfferResetContentAfter(
  row:
    | {
        canResetContentAfterExam?: boolean | null;
        canReattemptWithRewatch?: boolean | null;
        isCheckpointAnchored?: boolean | null;
      }
    | null
    | undefined,
): boolean {
  if (!row) return false;
  if (row.canResetContentAfterExam != null) return row.canResetContentAfterExam;
  return canOfferRewatchReattempt(row);
}

/**
 * Why the standalone reset is unavailable.
 *
 * Deliberately NOT `rewatchBlockedReason`, even though the condition is the same one: that copy ends
 * by pointing the operator at "New Attempt + Keep Progress", which is a reattempt and therefore no
 * substitute for this operation at all. Same rule, different remedy — and there is no remedy here, so
 * the wording stops at the reason.
 */
export function resetContentAfterBlockedReason(row: {
  examFinalCategory?: string | null;
  isCheckpointAnchored?: boolean | null;
}): string {
  if (row.examFinalCategory && row.examFinalCategory !== "None")
    return `This is a ${examFinalCategoryLabel(row.examFinalCategory)}, so there is no content after it to reset.`;

  if (row.isCheckpointAnchored === false)
    return "This exam is not attached to a checkpoint, so there is no content after it to reset.";

  return "There is no content after this exam to reset.";
}

/** Whether the progress-preserving reattempt may be offered. True for every exam, finals included. */
export function canOfferPreservingReattempt(
  row: { canReattemptPreservingProgress?: boolean | null } | null | undefined,
): boolean {
  if (!row) return false;
  return row.canReattemptPreservingProgress ?? true;
}

/**
 * Why the rewatch reattempt is unavailable, phrased as the next step the leader should take.
 *
 * Prefers the server's own reason so the menu tooltip and the API refusal read identically; falls
 * back to locally-derived wording only when the field is absent.
 */
export function rewatchBlockedReason(row: {
  attemptStatus?: string | null;
  examFinalCategory?: string | null;
  rewatchBlockedReason?: string | null;
  isCheckpointAnchored?: boolean | null;
}): string {
  // Status first: it is the condition the operator can actually clear right now.
  const s = (row.attemptStatus ?? "").toLowerCase().replace(/_/g, "");
  if (s === "inprogress") return "The attempt is still in progress — force-submit it first.";
  if (s === "pendinggrading")
    return "Grade the written answers first, then a new attempt can be authorized.";
  if (row.attemptStatus && !canReattemptFromStatus(row.attemptStatus))
    return "Only a finished attempt can be reattempted.";

  if (row.rewatchBlockedReason) return row.rewatchBlockedReason;

  if (row.examFinalCategory && row.examFinalCategory !== "None")
    return (
      `This is a ${examFinalCategoryLabel(row.examFinalCategory)}, so there is no content after it ` +
      "to redo. Use “New Attempt + Keep Progress” instead."
    );

  if (row.isCheckpointAnchored === false)
    return (
      "This exam is not attached to a checkpoint, so there is no content after it to reset. " +
      "Use “New Attempt + Keep Progress” instead."
    );

  return "This exam cannot be reattempted with a content rewatch.";
}

/**
 * The one function every surface asks "may this row take these operations, and why not".
 *
 * Five surfaces render these menus — the admin monitor's dialog footer and its two row menus, the
 * per-exam roster, the cross-exam attempts feed, the Student Sheet and the student timeline. Each of
 * them previously combined the guards itself, which is exactly how the admin monitor's live-session
 * menu ended up with no guards at all while the roster next to it had them. One helper, so a surface
 * can get the LAYOUT wrong but not the rules.
 *
 * Accepts the union of both row shapes the codebase uses: the TeamLeader DTOs are camelCase, and the
 * admin monitor's session mapper produces snake_case. Reading both here beats making every caller
 * remember to translate.
 */
export interface ReattemptRowFacts {
  attemptStatus?: string | null;
  sessionId?: string | null;
  canReattemptWithRewatch?: boolean | null;
  canReattemptPreservingProgress?: boolean | null;
  canResetContentAfterExam?: boolean | null;
  isCheckpointAnchored?: boolean | null;
  examFinalCategory?: string | null;
  rewatchBlockedReason?: string | null;
}

export interface ReattemptAvailability {
  rewatch: { allowed: boolean; reason: string };
  keepProgress: { allowed: boolean; reason: string };
  /**
   * The standalone downstream reset. Named on this type alongside the two reattempts because every
   * surface asks about all three at once, and a separate helper would be a second place for a surface
   * to forget one — which is the failure this type was introduced to stop.
   */
  resetContentAfter: { allowed: boolean; reason: string };
}

export function reattemptAvailabilityFor(
  row: ReattemptRowFacts | null | undefined,
): ReattemptAvailability {
  const blocked = (reason: string): ReattemptAvailability => ({
    rewatch: { allowed: false, reason },
    keepProgress: { allowed: false, reason },
    resetContentAfter: { allowed: false, reason },
  });

  if (!row) return blocked("Select an attempt first.");

  // Both operations are session-scoped: the session identifies which pass is superseded and which
  // gate re-closes. The student timeline moderates from audit events, some of which carry no session.
  // All three operations are addressed by session id — the reset endpoint resolves the student and the
  // exam from the sitting rather than taking them separately, so a row with no session cannot drive
  // any of them.
  if (row.sessionId === null || row.sessionId === "")
    return blocked("This event has no attempt to act on.");

  // ABSENT status is not a blocked status. The student attempt timeline moderates from audit events,
  // which carry no attempt status at all — guessing one there would hide legal actions, so an unknown
  // status defers to the server exactly as `canGradeRow` does for an unknown grading verdict. A
  // status that IS present and wrong still blocks, with a reason naming the next step.
  const statusOk = !row.attemptStatus || canReattemptFromStatus(row.attemptStatus);

  return {
    rewatch: {
      allowed: statusOk && canOfferRewatchReattempt(row),
      reason: rewatchBlockedReason(row),
    },
    keepProgress: {
      allowed: statusOk && canOfferPreservingReattempt(row),
      reason: preservingBlockedReason(row.attemptStatus),
    },
    // NOT status-gated, and that is the point of it being a separate operation. The two reattempts
    // re-close the gate the student is standing behind, so a live or awaiting-grading sitting has to
    // clear first; this one leaves the gate open and the ledger alone, so a sitting in flight is
    // simply unrelated. `ExamService.ResetContentAfterExamAsync` imposes no status precondition
    // either — adding one here would hide an action the server would happily perform.
    resetContentAfter: {
      allowed: canOfferResetContentAfter(row),
      reason: resetContentAfterBlockedReason(row),
    },
  };
}

/** Why the progress-preserving reattempt is unavailable. Status is its only precondition. */
export function preservingBlockedReason(attemptStatus: string | null | undefined): string {
  const s = (attemptStatus ?? "").toLowerCase().replace(/_/g, "");
  if (s === "inprogress") return "The attempt is still in progress — force-submit it first.";
  if (s === "pendinggrading")
    return "Grade the written answers first, then a new attempt can be authorized.";
  return "Only a finished attempt can be reattempted.";
}

/**
 * Canonical PascalCase for an attempt status arriving in any of the shapes the surfaces use
 * ("Abandoned", "abandoned", "pending_grading"). Returns undefined when there is nothing to match,
 * so callers fall through to status-neutral copy rather than guessing.
 */
export function normaliseAttemptStatus(
  attemptStatus: string | null | undefined,
): AttemptStatus | undefined {
  if (!attemptStatus) return undefined;
  const flat = attemptStatus.toLowerCase().replace(/_/g, "");
  return ATTEMPT_STATUSES.find((s) => s.toLowerCase() === flat);
}

export const FORCE_SUBMIT_BLOCKED_REASON =
  "Only an attempt that is still in progress can be force-submitted. An attempt awaiting grading " +
  "has already been handed in — grade its written answers to finalize it.";

// ── Confirm-dialog copy ───────────────────────────────────────────────────────

/**
 * The per-attempt moderation actions.
 *
 * Named for what happens to the work the student did AFTER the exam, rather than for the mechanism
 * each one uses. That is the distinction an operator actually has to make at the point of clicking,
 * and naming by mechanism is what previously made two of these indistinguishable.
 *
 * `reset` is deliberately not one of the reattempt operations: it says "this sitting was technically
 * invalid, do not charge them for it" and touches no gate at all.
 */
export type RowActionKind =
  "rewatchReattempt" | "keepProgressReattempt" | "resetContentAfter" | "reset" | "forceSubmit";

/**
 * The minimum a surface must know about an attempt to moderate it.
 *
 * Structural, not nominal, so a roster row satisfies it as-is — while the student attempt timeline,
 * whose rows are audit events rather than sessions, can satisfy it too. Before this, the timeline
 * carried its own copy of the four mutations, its own confirm dialog and its own (differently
 * worded) copy; one description of what "reset" does is enough.
 */
export interface ModerationTarget {
  studentId: string;
  sessionId?: string | null;
  /** Falls back to the surrounding exam when the target does not name one. */
  examId?: string | null;
  studentName?: string | null;
  email?: string | null;
  /**
   * The attempt's current status, when the surface knows it. Roster rows carry it; the student
   * attempt timeline does not (its rows are audit events, not sessions), so anything reading this
   * must tolerate `undefined` and fall back to status-neutral wording.
   *
   * Used only to pick accurate confirm copy — never to decide authorization.
   */
  attemptStatus?: string | null;
  /**
   * Whether the exam is anchored to a progression checkpoint, as decided by the SERVER. Governs
   * whether the two content-resetting actions are offered at all. Optional for the same reason
   * `attemptStatus` is: not every surface has it, and absent means "offer it and let the server
   * decide".
   */
  isCheckpointAnchored?: boolean | null;
}

export interface PendingRowAction {
  kind: RowActionKind;
  target: ModerationTarget;
}

export type BulkActionKind = "bulk-reset" | "bulk-retake";

export const ROW_ACTION_COPY: Record<
  RowActionKind,
  {
    title: string;
    description: (target: ModerationTarget) => string;
    needsReason: boolean;
    /** Where the reason ends up. Only some actions' reasons reach the student. */
    reasonHint?: string;
    /**
     * How hard this is to walk back, driving the warning banner in the confirm dialog.
     * "destructive" = irreversible; "caution" = undoable only by hand. Absent = neither, and the
     * dialog deliberately shows nothing rather than a reassurance nobody asked for.
     */
    severity?: "destructive" | "caution";
  }
> = {
  // ── Reset vs. Retake ────────────────────────────────────────────────────────────────────────
  // These two look interchangeable and are not, so the copy has to carry the distinction the UI
  // otherwise cannot: they are different terms of the same sum
  // (attemptsRemaining = 1 + grants - attemptsUsed). Reset discounts the attempt that was already
  // spent; Retake buys an extra one on top of it. Running both gives the student TWO sittings,
  // which is why the server now refuses the second as a no-op rather than silently obliging.
  //
  // Wording mirrors ExamService.ResetAttemptForTeamLeaderAsync, which explicitly preserves history
  // ("never touch Score/Passed/Responses") and only sets ExcludedFromAttemptCount. Earlier copy
  // said the attempt was "cleared" and that this "cannot be undone" — both untrue, and alarming
  // enough to stop a team leader using a safe, reversible action.
  reset: {
    title: "Reset attempt?",
    description: (t) =>
      `Use when the sitting itself was invalid — a crash, a lockout, a technical fault — and should not be held against ${t.studentName || t.email}. It stops counting towards their attempt limit, which frees them to sit the exam again. The attempt itself (score, pass/fail and answers) is kept in full and stays visible in the roster and their history.`,
    needsReason: true,
  },
  // ── The two reattempt operations ────────────────────────────────────────────────────────────
  // Same three facts, in the same order, in both: what the student gets, what happens to the work
  // after the exam, and what happens to their history. Told the same way so the ONE difference
  // between them is the only thing that reads as different.
  rewatchReattempt: {
    title: "New attempt, and redo the later content?",
    description: (t) => {
      const who = t.studentName || t.email;
      return (
        `${who} gets one new attempt at this exam. Everything they completed AFTER it is reset and ` +
        "locked, so once they pass they will have to work through that content again. Their previous " +
        "attempts stay in their history exactly as they are — nothing is deleted. If they have " +
        "already passed on a LATER attempt than this one, that pass is not affected and nothing is " +
        "locked."
      );
    },
    needsReason: true,
    reasonHint: "Shown to the student when they return to the exam, and kept in the audit trail",
    severity: "caution",
  },
  keepProgressReattempt: {
    title: "New attempt, keeping their progress?",
    description: (t) => {
      const who = t.studentName || t.email;
      return (
        `${who} gets one new attempt at this exam. Everything after it is locked until they pass, ` +
        "but none of their progress is lost — it comes straight back, with nothing to rewatch. Their " +
        "previous attempts stay in their history exactly as they are. If they have already passed on " +
        "a LATER attempt than this one, that pass is not affected and nothing is locked."
      );
    },
    needsReason: true,
    reasonHint: "Shown to the student when they return to the exam, and kept in the audit trail",
  },
  // ── The reset without a reattempt ───────────────────────────────────────────────────────────
  // The third of the three "content after the exam" operations, and the only one that changes nothing
  // about the exam. Its copy has one job the other two do not: saying out loud that the student is NOT
  // being asked to sit the exam again, because every other action in this menu does exactly that and an
  // operator reading quickly will assume this one does too.
  resetContentAfter: {
    title: "Reset the content after this exam?",
    description: (t) => {
      const who = t.studentName || t.email;
      return (
        `Everything ${who} completed AFTER this exam is reset, so they have to work through it again. ` +
        "Their exam result does not change and they are NOT given another attempt — the content stays " +
        "unlocked, they just have to redo it. Watch progress cannot be restored afterwards."
      );
    },
    needsReason: true,
    reasonHint: "Shown to the student with the notification, and kept in the audit trail",
    severity: "caution",
  },
  // One state only. PendingGrading used to be accepted here too, and the copy had to describe both —
  // but the endpoint never actually finalized such an attempt (CompleteExamAsync short-circuits on
  // any non-InProgress status), so the state has been removed rather than described.
  forceSubmit: {
    title: "Force-submit this attempt?",
    description: (t) =>
      `Ends ${t.studentName || t.email}'s attempt now and scores the answers they have submitted so far. Unanswered questions score nothing. This cannot be undone.`,
    needsReason: false,
    severity: "destructive",
  },
};

// ── Menu-level explanations ───────────────────────────────────────────────────
// One line per action, shown UNDER the label in the Exam Monitor "All Actions" menu — so the
// operator reads what an action does *before* opening its dialog, not after.
//
// Every claim below was read off the handler named in `handler`, not off the button label. A label
// can imply the opposite of what its service does, which is why this exists: "Reset Attempt" reads
// as "wipe the attempt" and the service explicitly preserves Score/Passed/Responses.

/**
 * Everything the Exam Monitor's "All Actions" menu offers — the four moderation actions plus the
 * two that are not moderation at all (grading and messaging) and so have no confirm dialog.
 */
export type MonitorActionKind = RowActionKind | "gradeEssay" | "messageStudent";

export interface MonitorActionHint {
  /** The menu label, verbatim, so the hint cannot drift from the item it sits under. */
  label: string;
  /** One line: what this does. Status-aware where the behaviour genuinely differs by status. */
  hint: (attemptStatus?: string | null) => string;
  /** A second, shorter line for the caveat that would otherwise be a nasty surprise. */
  caveat?: string;
  /**
   * "destructive" = irreversible; "caution" = reversible only by hand. Drives the red/amber
   * treatment in the menu and matches the warning the confirm dialog shows.
   */
  severity?: "destructive" | "caution";
  /** The server-side method that actually runs. Kept here so the copy has a citation to check. */
  handler: string;
}

export const MONITOR_ACTION_HINTS: Record<MonitorActionKind, MonitorActionHint> = {
  // The two reattempt operations sit together at the top of every menu, labelled as siblings, with
  // the differing half of the label last — so the choice reads as "which of these two", not as two
  // unrelated actions.
  rewatchReattempt: {
    label: "New Attempt + Rewatch Content",
    hint: () =>
      "Gives the student one new attempt, and resets what they completed after this exam so they " +
      "work through it again.",
    caveat:
      "The later content is locked until they pass, and they will have to redo it. Their previous " +
      "attempts and scores are kept, and a pass earned on a later attempt than this one is left " +
      "standing.",
    severity: "caution",
    handler:
      "POST /teamleader/exams/sessions/{id}/reattempt-with-rewatch -> ExamService.ReattemptWithRewatchAsync",
  },
  keepProgressReattempt: {
    label: "New Attempt + Keep Progress",
    hint: () =>
      "Gives the student one new attempt, and keeps everything they completed after this exam.",
    caveat:
      "The later content is locked until they pass, then comes back exactly as it was — nothing to " +
      "rewatch. A pass earned on a later attempt than this one is left standing. The only option " +
      "for final exams.",
    handler:
      "POST /teamleader/exams/sessions/{id}/reattempt-keep-progress -> ExamService.ReattemptPreservingProgressAsync",
  },
  resetContentAfter: {
    label: "Reset Content After This Exam",
    hint: () =>
      "Resets what the student completed after this exam so they work through it again, and changes " +
      "nothing about the exam itself.",
    caveat:
      "No new attempt, and their result and score stay as they are. The content is not locked — it " +
      "just has to be redone. Watch progress cannot be restored.",
    severity: "caution",
    handler:
      "POST /teamleader/exams/sessions/{id}/reset-content-after -> ExamService.ResetContentAfterExamAsync",
  },
  reset: {
    label: "Reset Attempt",
    hint: () =>
      "Stops this attempt counting towards the student's attempt limit, which frees them to sit " +
      "the exam again.",
    caveat:
      "Nothing is deleted — the score, pass/fail and answers stay visible. Refused if the student " +
      "can already re-sit, so it cannot hand out a second extra attempt.",
    handler:
      "POST /teamleader/exams/sessions/{id}/reset -> ExamService.ResetAttemptForTeamLeaderAsync",
  },
  forceSubmit: {
    label: "Force Submit",
    // No longer status-aware: PendingGrading is no longer an accepted state, so there is only one
    // thing this can mean.
    hint: () =>
      "Ends the student's live attempt now and scores the answers they have submitted so far.",
    caveat: "Cannot be undone — the attempt is scored and closed.",
    severity: "destructive",
    handler:
      "POST /teamleader/exams/sessions/{id}/force-submit -> ExamService.ForceSubmitForTeamLeaderAsync",
  },
  gradeEssay: {
    label: "Grade Essay",
    hint: () => "Opens the written answers so you can award marks and leave a note.",
    caveat: "Saving a mark updates the student's score for this attempt.",
    handler:
      "POST /admin/grading-queue/{responseId}/grade -> GradingService.GradeEssayResponseAsync",
  },
  messageStudent: {
    label: "Message Student",
    hint: () => "Sends this one student a direct message in the LMS.",
    caveat: "Changes nothing about the attempt or the exam.",
    handler: "POST /teamleader/exams/bulk/message -> TeamLeaderExamService.SendBulkMessageAsync",
  },
};
